import { useNavigate } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { SizeSet } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useDeleteSizeSet } from '../hooks/use-size-set-mutations';

/** Delete from the editor's rail; a 409 RESOURCE_IN_USE stays on the page, explained. */
export function SizeSetDangerZone({ sizeSet }: { sizeSet: SizeSet }) {
  const { t } = useTranslation(['sizeSets', 'common', 'errors']);
  const navigate = useNavigate();
  const remove = useDeleteSizeSet();
  const message = useErrorMessage();
  const error = isApiError(remove.error) ? remove.error : null;

  return (
    <FormSection title={t('sizeSets:danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('sizeSets:danger.description')}</p>
      {error ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {error.code === 'RESOURCE_IN_USE' ? t('sizeSets:danger.inUse') : message(error)}
            {error.requestId ? (
              <span className="block text-xs">
                {t('errors:requestIdShort', { requestId: error.requestId })}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="text-destructive hover:text-destructive w-full"
            disabled={remove.isPending}
          >
            <Trash2 aria-hidden />
            {t('sizeSets:danger.delete')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('sizeSets:delete.title', { name: sizeSet.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('sizeSets:delete.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                remove.mutate(sizeSet.id, {
                  onSuccess: () => {
                    // The set is gone; a dirty editor must not keep us on its page.
                    releaseSaveBar();
                    void navigate({ to: '/org/catalog-setup/size-sets' });
                  },
                })
              }
            >
              {remove.isPending ? t('sizeSets:delete.submitting') : t('sizeSets:delete.submit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormSection>
  );
}
